import React, { useState, useCallback } from 'react';
import api from '@/utils/api';
import { toastMessage } from '@/utils/toast-message';
import { RefreshCw, ShoppingCart, Phone, Zap, Repeat, Info, X } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AbcRow {
  date: string;
  gk_total_events: number;
  gk_unique_phones: number;
  gk_new_lead_phones: number;
  gk_updated_phones: number;
  gk_webhook_duplicate: number;
  gk_webhook_error: number;
  gk_no_phone: number;
  sh_total_events: number;
  sh_unique_phones: number;
  sh_new_lead_phones: number;
  sh_updated_phones: number;
  sh_checkout_duplicate: number;
  total_unique: number;
  tab_abc: number;
  tab_reorder: number;
  tab_agent: number;
}

type Totals = Omit<AbcRow, 'date'>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => toISO(new Date());
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISO(d);
};

const num = (v: number) => v.toLocaleString('en-IN');

// ─── Summary Card ─────────────────────────────────────────────────────────────

const Card = ({ label, value, sub, icon, color, border }: {
  label: string; value: number; sub?: string;
  icon: React.ReactNode; color: string; border: string;
}) => (
  <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 ${border} px-3 py-2.5 flex items-center gap-2.5`}>
    <div className={`p-1.5 rounded-md flex-shrink-0 ${color}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
      <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{num(value)}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{sub}</p>}
    </div>
  </div>
);

// ─── Badge ────────────────────────────────────────────────────────────────────

const Badge = ({ value, color }: { value: number; color: string }) => (
  <span className={`inline-block min-w-[2rem] text-center px-1.5 py-0.5 rounded text-xs font-medium ${
    value === 0 ? 'text-gray-400 dark:text-gray-600' : color
  }`}>
    {num(value)}
  </span>
);

// ─── Info Modal ───────────────────────────────────────────────────────────────

const InfoModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-brandColor" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">How to read this report</h2>
        </div>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      <div className="px-5 py-4 space-y-5 text-sm text-gray-700 dark:text-gray-300">

        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-semibold rounded">GoKwik</span>
          </div>
          <div className="space-y-1.5 pl-1">
            <p><span className="font-medium text-gray-900 dark:text-white">Total Events</span> — Raw webhook hits GoKwik sent us that day.</p>
            <p><span className="font-medium text-gray-900 dark:text-white">Unique Phones</span> — Leads whose <span className="font-mono text-xs">last_abc_date</span> was set by a GoKwik event on this day.</p>
            <p><span className="font-medium text-blue-600">New Lead</span> — Of those, leads that were created fresh.</p>
            <p><span className="font-medium text-yellow-600">Updated</span> — Of those, leads that already existed and were updated.</p>
            <p><span className="font-medium text-red-500">Failed / No Phone</span> — Webhook errors or missing phone.</p>
          </div>
          <div className="mt-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-xs font-mono text-purple-800 dark:text-purple-200">
            Unique = New Lead + Updated
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-700" />

        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold rounded">Shopify ABC</span>
          </div>
          <div className="space-y-1.5 pl-1">
            <p><span className="font-medium text-gray-900 dark:text-white">Total Events</span> — Shopify abandoned checkout webhooks received.</p>
            <p><span className="font-medium text-gray-900 dark:text-white">Unique Phones</span> — Leads whose <span className="font-mono text-xs">last_abc_date</span> was set by a Shopify event on this day.</p>
            <p><span className="font-medium text-blue-600">New Lead</span> — Created fresh from Shopify.</p>
            <p><span className="font-medium text-yellow-600">Updated</span> — Updated an existing lead.</p>
          </div>
          <div className="mt-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg text-xs font-mono text-green-800 dark:text-green-200">
            Unique = New Lead + Updated
          </div>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-700" />

        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-semibold rounded">Lead Buckets</span>
            <span className="text-xs text-gray-500">← what agents see when filtering by Last ABC Date</span>
          </div>
          <div className="space-y-1.5 pl-1">
            <p><span className="font-medium text-indigo-600">ABC</span> — Leads in the Abandoned Cart bucket. Matches the Leads tab filter exactly.</p>
            <p><span className="font-medium text-orange-600">Reorder</span> — Reorder leads that also had an ABC event that day.</p>
          </div>
          <div className="mt-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-xs text-indigo-800 dark:text-indigo-200 space-y-1">
            <p className="font-semibold font-mono">GK Unique + Shopify Unique = ABC + Reorder + Agent</p>
            <p className="text-gray-500 dark:text-gray-400 mt-1">All numbers are counted from <span className="font-mono">last_abc_date</span> on the lead — the same field the filter uses. They always match.</p>
          </div>
        </div>

      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AbcReport = () => {
  const [fromDate, setFromDate] = useState(daysAgo(14));
  const [toDate, setToDate] = useState(today);
  const [rows, setRows] = useState<AbcRow[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.req({
        url: '/reports/abc',
        method: api.Method.POST,
        data: { from_date: fromDate, to_date: toDate },
      }) as unknown as { data: AbcRow[]; totals: Totals };
      setRows(res.data || []);
      setTotals(res.totals || null);
      setLoaded(true);
    } catch {
      toastMessage('Failed to load ABC report', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  const t = totals;

  return (
    <>
    {showInfo && <InfoModal onClose={() => setShowInfo(false)} />}
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* ── Header ── */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-brandColor p-2 rounded-lg">
              <ShoppingCart className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">ABC Report</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                GoKwik &amp; Shopify abandoned cart — all counts from lead last_abc_date
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">From</label>
              <input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={e => setFromDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">To</label>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={e => setToDate(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brandColor"
              />
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-1.5 bg-brandColor text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>{loading ? 'Loading…' : 'Run'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 flex flex-col min-h-0 p-6 gap-6">

        {/* ── Summary cards ── */}
        {t && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card
              label="GoKwik Unique Phones"
              value={t.gk_unique_phones}
              sub={`${num(t.gk_new_lead_phones)} new · ${num(t.gk_updated_phones)} updated`}
              icon={<Zap className="h-3.5 w-3.5 text-purple-600" />}
              color="bg-purple-50 dark:bg-purple-900/20"
              border="border-l-purple-400"
            />
            <Card
              label="Shopify Unique Phones"
              value={t.sh_unique_phones}
              sub={`${num(t.sh_new_lead_phones)} new · ${num(t.sh_updated_phones)} updated`}
              icon={<Phone className="h-3.5 w-3.5 text-green-600" />}
              color="bg-green-50 dark:bg-green-900/20"
              border="border-l-green-400"
            />
            <Card
              label="Lead Buckets (ABC)"
              value={t.tab_abc}
              sub="Matches filter: Last ABC Date"
              icon={<ShoppingCart className="h-3.5 w-3.5 text-indigo-600" />}
              color="bg-indigo-50 dark:bg-indigo-900/20"
              border="border-l-indigo-400"
            />
            <Card
              label="Lead Buckets (Reorder)"
              value={t.tab_reorder}
              sub="ABC events on reorder leads"
              icon={<Repeat className="h-3.5 w-3.5 text-orange-600" />}
              color="bg-orange-50 dark:bg-orange-900/20"
              border="border-l-orange-400"
            />
          </div>
        )}

        {/* ── Empty / not-yet-loaded state ── */}
        {!loaded && !loading && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <ShoppingCart className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Select a date range and click <strong>Run Report</strong></p>
          </div>
        )}

        {/* ── Table ── */}
        {loaded && (
          <div className="flex-1 min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-auto h-full">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-2 text-left bg-gray-50 dark:bg-gray-900 text-gray-500 text-xs font-medium" rowSpan={2}>
                      <div className="flex items-center gap-1.5">
                        Date
                        <button
                          onClick={() => setShowInfo(true)}
                          className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          title="How to read this report"
                        >
                          <Info className="h-3.5 w-3.5 text-gray-400 hover:text-brandColor transition-colors" />
                        </button>
                      </div>
                    </th>
                    <th colSpan={4} className="px-4 py-2 text-center bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-xs font-semibold border-x border-purple-200 dark:border-purple-800">
                      GoKwik
                    </th>
                    <th colSpan={4} className="px-4 py-2 text-center bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs font-semibold border-x border-green-200 dark:border-green-800">
                      Shopify ABC
                    </th>
                    <th colSpan={4} className="px-4 py-2 text-center bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 text-xs font-semibold border-l border-indigo-200 dark:border-indigo-800">
                      Lead Buckets
                    </th>
                  </tr>
                  <tr className="border-b-2 border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {/* GoKwik */}
                    <th className="px-3 py-2 bg-purple-50 dark:bg-purple-900/10 whitespace-nowrap">Total Events</th>
                    <th className="px-3 py-2 bg-purple-50 dark:bg-purple-900/10 whitespace-nowrap">Unique Phones</th>
                    <th className="px-3 py-2 bg-purple-50 dark:bg-purple-900/10 whitespace-nowrap text-blue-600">New Lead</th>
                    <th className="px-3 py-2 bg-purple-50 dark:bg-purple-900/10 whitespace-nowrap border-r border-purple-200 dark:border-purple-800 text-yellow-600">Updated</th>
                    {/* Shopify */}
                    <th className="px-3 py-2 bg-green-50 dark:bg-green-900/10 whitespace-nowrap">Total Events</th>
                    <th className="px-3 py-2 bg-green-50 dark:bg-green-900/10 whitespace-nowrap">Unique Phones</th>
                    <th className="px-3 py-2 bg-green-50 dark:bg-green-900/10 whitespace-nowrap text-blue-600">New Lead</th>
                    <th className="px-3 py-2 bg-green-50 dark:bg-green-900/10 whitespace-nowrap border-r border-green-200 dark:border-green-800 text-yellow-600">Updated</th>
                    {/* Lead Buckets */}
                    <th className="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/10 whitespace-nowrap font-semibold text-indigo-800 dark:text-indigo-200 border-l border-indigo-200 dark:border-indigo-800">Total</th>
                    <th className="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/10 whitespace-nowrap text-indigo-700 dark:text-indigo-300">ABC</th>
                    <th className="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/10 whitespace-nowrap text-indigo-700 dark:text-indigo-300">Reorder</th>
                    <th className="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/10 whitespace-nowrap text-indigo-700 dark:text-indigo-300">Agent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((r, i) => (
                    <tr key={r.date} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${i % 2 === 0 ? '' : 'bg-gray-50/40 dark:bg-gray-800/50'}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white whitespace-nowrap">{r.date}</td>
                      {/* GoKwik */}
                      <td className="px-3 py-2.5 text-center"><Badge value={r.gk_total_events} color="text-purple-700 bg-purple-50 dark:text-purple-300 dark:bg-purple-900/20" /></td>
                      <td className="px-3 py-2.5 text-center"><Badge value={r.gk_unique_phones} color="text-purple-700 bg-purple-50 dark:text-purple-300 dark:bg-purple-900/20" /></td>
                      <td className="px-3 py-2.5 text-center"><Badge value={r.gk_new_lead_phones} color="text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/20" /></td>
                      <td className="px-3 py-2.5 text-center border-r border-gray-100 dark:border-gray-700"><Badge value={r.gk_updated_phones} color="text-yellow-700 bg-yellow-50 dark:text-yellow-300 dark:bg-yellow-900/20" /></td>
                      {/* Shopify */}
                      <td className="px-3 py-2.5 text-center"><Badge value={r.sh_total_events} color="text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/20" /></td>
                      <td className="px-3 py-2.5 text-center"><Badge value={r.sh_unique_phones} color="text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/20" /></td>
                      <td className="px-3 py-2.5 text-center"><Badge value={r.sh_new_lead_phones} color="text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/20" /></td>
                      <td className="px-3 py-2.5 text-center border-r border-gray-100 dark:border-gray-700"><Badge value={r.sh_updated_phones} color="text-yellow-700 bg-yellow-50 dark:text-yellow-300 dark:bg-yellow-900/20" /></td>
                      {/* Lead Buckets */}
                      <td className="px-3 py-2.5 text-center border-l border-gray-100 dark:border-gray-700"><Badge value={r.total_unique} color="text-indigo-800 bg-indigo-100 dark:text-indigo-200 dark:bg-indigo-900/30" /></td>
                      <td className="px-3 py-2.5 text-center"><Badge value={r.tab_abc} color="text-indigo-700 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-900/20" /></td>
                      <td className="px-3 py-2.5 text-center"><Badge value={r.tab_reorder} color="text-indigo-700 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-900/20" /></td>
                      <td className="px-3 py-2.5 text-center"><Badge value={r.tab_agent} color="text-indigo-700 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-900/20" /></td>
                    </tr>
                  ))}

                  {/* Totals row */}
                  {t && rows.length > 1 && (
                    <tr className="bg-gray-100 dark:bg-gray-700 border-t-2 border-gray-300 dark:border-gray-500 font-semibold text-gray-900 dark:text-white">
                      <td className="px-4 py-3 text-xs font-bold uppercase tracking-wide">TOTAL</td>
                      <td className="px-3 py-3 text-center text-sm">{num(t.gk_total_events)}</td>
                      <td className="px-3 py-3 text-center text-sm">{num(t.gk_unique_phones)}</td>
                      <td className="px-3 py-3 text-center text-sm text-blue-700 dark:text-blue-300">{num(t.gk_new_lead_phones)}</td>
                      <td className="px-3 py-3 text-center text-sm text-yellow-600 border-r border-gray-300 dark:border-gray-500">{num(t.gk_updated_phones)}</td>
                      <td className="px-3 py-3 text-center text-sm">{num(t.sh_total_events)}</td>
                      <td className="px-3 py-3 text-center text-sm">{num(t.sh_unique_phones)}</td>
                      <td className="px-3 py-3 text-center text-sm text-blue-700 dark:text-blue-300">{num(t.sh_new_lead_phones)}</td>
                      <td className="px-3 py-3 text-center text-sm text-yellow-600 border-r border-gray-300 dark:border-gray-500">{num(t.sh_updated_phones)}</td>
                      <td className="px-3 py-3 text-center text-sm font-semibold text-indigo-800 dark:text-indigo-200 border-l border-gray-300 dark:border-gray-500">{num(t.total_unique)}</td>
                      <td className="px-3 py-3 text-center text-sm text-indigo-700 dark:text-indigo-300">{num(t.tab_abc)}</td>
                      <td className="px-3 py-3 text-center text-sm text-indigo-700 dark:text-indigo-300">{num(t.tab_reorder)}</td>
                      <td className="px-3 py-3 text-center text-sm text-indigo-700 dark:text-indigo-300">{num(t.tab_agent)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {rows.length === 0 && (
              <div className="text-center py-12 text-gray-400 text-sm">No data for selected range</div>
            )}
          </div>
        )}

      </div>
    </div>
    </>
  );
};

export default AbcReport;
